import { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Upload, X, AlertCircle, Loader2, Sparkles, Crop, Wand2, Check, Star, Info } from 'lucide-react';
import type { Step3Data, LogoSource } from '../../../types/onboarding';
import {
  uploadLogo,
  generateLogo,
  selectLogo,
  getLogoGenerations,
  getLogoGenerationMode,
  LogoStyle,
  LogoGeneration,
  LogoGenerationMode,
} from '../../../api/onboarding';
import { extractColorsFromImage, ExtractedColors } from '../../../utils/colorExtractor';
import { LogoCropper } from '../LogoCropper';

interface Step3Props {
  data: Step3Data | null;
  onChange: (data: Step3Data) => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  sessionId: string;
}

// Color presets - designed with distinct primary/secondary pairs for visual contrast
const COLOR_PRESETS = [
  { name: 'Ocean', primary: '#2563EB', secondary: '#0D9488' },      // Blue + Teal
  { name: 'Sunset', primary: '#D97706', secondary: '#DC2626' },     // Amber + Red
  { name: 'Forest', primary: '#059669', secondary: '#1E3A5F' },     // Green + Navy
  { name: 'Berry', primary: '#7C3AED', secondary: '#DB2777' },      // Purple + Pink
  { name: 'Classic', primary: '#1E3A5F', secondary: '#D97706' },    // Navy + Gold
  { name: 'Modern', primary: '#18181B', secondary: '#3AABE8' },     // Charcoal + Sky Blue (niftee colors)
];

// Logo style options for AI generation
const LOGO_STYLES: Array<{ value: LogoStyle; label: string; description: string }> = [
  { value: 'classic', label: 'Classic', description: 'Timeless, traditional feel' },
  { value: 'modern', label: 'Modern', description: 'Clean, contemporary design' },
  { value: 'bold', label: 'Bold', description: 'Strong, attention-grabbing' },
];

type LogoMode = 'upload' | 'generate' | 'none';

/**
 * The AI logo builder is hidden while its prompting is reworked — the output
 * was not good enough to put in front of a customer as their brand mark.
 *
 * Hidden, not deleted: the backend (`logo-generation.ts`) and the generate
 * panel below are left intact, so re-enabling is this one constant. Existing
 * AI-generated logos keep working and stay visible; only the entry point is
 * withdrawn. LOGO_PRICE_CENTS is already 0, so nothing billable is affected.
 */
const LOGO_GENERATOR_ENABLED = false;

export function Step3BrandAssets({
  data,
  onChange,
  onNext,
  onBack,
  saving,
  sessionId,
}: Step3Props) {
  // Logo state
  const [logoMode, setLogoMode] = useState<LogoMode>(
    data?.logo_url
      ? (data.logo_source === 'ai-generated' && LOGO_GENERATOR_ENABLED ? 'generate' : 'upload')
      : (data?.has_logo === false ? 'none' : 'upload')
  );
  const [logoUrl, setLogoUrl] = useState(data?.logo_url || '');
  const [logoSource, setLogoSource] = useState<LogoSource | undefined>(data?.logo_source);

  // Color state
  const [primaryColor, setPrimaryColor] = useState(data?.primary_color || '#D97706');
  const [secondaryColor, setSecondaryColor] = useState(data?.secondary_color || '#B45309');
  const [extractingColors, setExtractingColors] = useState(false);
  const [extractedColors, setExtractedColors] = useState<ExtractedColors | null>(
    data?.extracted_colors || null
  );
  const [showExtractedColors, setShowExtractedColors] = useState(!!(data?.extracted_colors));

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [tempUploadFile, setTempUploadFile] = useState<File | null>(null);

  // AI generation state
  const [generationMode, setGenerationMode] = useState<LogoGenerationMode | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<LogoStyle>('modern');
  const [userHint, setUserHint] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generations, setGenerations] = useState<LogoGeneration[]>([]);
  const [generationsUsed, setGenerationsUsed] = useState(0);
  const [generationsAllowed, setGenerationsAllowed] = useState(3);
  const [selectedGeneration, setSelectedGeneration] = useState<string | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [satisfactionRating, setSatisfactionRating] = useState<number | null>(null);
  const [showColorConfirm, setShowColorConfirm] = useState(false);

  // Check generation mode on mount
  useEffect(() => {
    getLogoGenerationMode().then(setGenerationMode);
  }, []);

  // Load existing generations on mount
  useEffect(() => {
    if (sessionId) {
      getLogoGenerations(sessionId).then((result) => {
        if (result.generations) {
          setGenerations(result.generations);
          const selected = result.generations.find(g => g.isSelected);
          if (selected) {
            setSelectedGeneration(selected.id);
            setLogoUrl(selected.logoUrl);
            setLogoSource('ai-generated');
          }
        }
        if (result.order) {
          setGenerationsUsed(result.order.generationsUsed);
          setGenerationsAllowed(result.order.generationsAllowed);
        }
      }).catch(console.error);
    }
  }, [sessionId]);

  // Save current state to parent
  const saveData = (updates: Partial<Step3Data> = {}) => {
    const hasLogo = logoMode !== 'none' && !!logoUrl;
    onChange({
      has_logo: updates.has_logo ?? hasLogo,
      logo_url: updates.logo_url ?? (logoUrl || undefined),
      logo_source: updates.logo_source ?? logoSource,
      primary_color: updates.primary_color ?? primaryColor,
      secondary_color: updates.secondary_color ?? secondaryColor,
      extracted_colors: updates.extracted_colors ?? extractedColors ?? undefined,
    });
  };

  // ============================================
  // Upload handlers
  // ============================================

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 5MB.');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload PNG, JPG, SVG, or WebP.');
      return;
    }

    setUploadError(null);
    setTempUploadFile(file);

    if (file.type === 'image/svg+xml') {
      await uploadLogoFile(file);
      return;
    }

    const tempUrl = URL.createObjectURL(file);
    setTempImageUrl(tempUrl);
    setShowCropper(true);
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setShowCropper(false);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
    }

    const croppedFile = new File([croppedBlob], 'logo.png', { type: 'image/png' });
    await uploadLogoFile(croppedFile);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
    }
    setTempUploadFile(null);
  };

  const handleCropSkip = async () => {
    setShowCropper(false);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
    }
    if (tempUploadFile) {
      await uploadLogoFile(tempUploadFile);
      setTempUploadFile(null);
    }
  };

  const uploadLogoFile = async (file: File) => {
    setUploading(true);
    setUploadError(null);

    try {
      const result = await uploadLogo(sessionId, file);
      setLogoUrl(result.logoUrl);
      setLogoSource('upload');

      setExtractingColors(true);
      const colors = await extractColorsFromImage(result.logoUrl);
      setExtractingColors(false);

      if (colors) {
        setExtractedColors(colors);
        setShowExtractedColors(true);
        setPrimaryColor(colors.primary);
        setSecondaryColor(colors.secondary);
        saveData({
          logo_url: result.logoUrl,
          logo_source: 'upload',
          has_logo: true,
          primary_color: colors.primary,
          secondary_color: colors.secondary,
          extracted_colors: colors,
        });
      } else {
        saveData({ logo_url: result.logoUrl, logo_source: 'upload', has_logo: true });
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to upload logo');
      setExtractingColors(false);
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // AI generation handlers
  // ============================================

  const handleGenerateClick = () => {
    if (generationsUsed >= generationsAllowed && generationsAllowed > 0) {
      setGenerateError('All logo options have been generated. Please select one.');
      return;
    }

    // For first generation, show color confirmation
    if (generations.length === 0) {
      setShowColorConfirm(true);
      return;
    }

    // Otherwise, generate directly
    doGenerate();
  };

  const doGenerate = async () => {
    setShowColorConfirm(false);
    setGenerating(true);
    setGenerateError(null);

    try {
      const result = await generateLogo(sessionId, selectedStyle, userHint);

      // Add to generations list
      const newGen: LogoGeneration = {
        id: result.generationId,
        roundNumber: result.roundNumber,
        style: selectedStyle,
        logoUrl: result.logoUrl,
        isSelected: false,
      };
      setGenerations(prev => [...prev, newGen]);
      setGenerationsUsed(result.roundNumber);
      setGenerationsAllowed(result.remainingRounds + result.roundNumber);

      // Auto-select the new generation for preview
      setSelectedGeneration(result.generationId);
      setLogoUrl(result.logoUrl);
      setLogoSource('ai-generated');

    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Failed to generate logo');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectGeneration = async (genId: string) => {
    const gen = generations.find(g => g.id === genId);
    if (!gen) return;

    setSelectedGeneration(genId);
    setLogoUrl(gen.logoUrl);
    setLogoSource('ai-generated');
  };

  const handleConfirmSelection = async () => {
    if (!selectedGeneration) return;

    setSelecting(true);
    try {
      const result = await selectLogo(sessionId, selectedGeneration, satisfactionRating || undefined);
      saveData({
        logo_url: result.logoUrl,
        logo_source: 'ai-generated',
        has_logo: true,
      });

      // Update generations list to show selection
      setGenerations(prev => prev.map(g => ({
        ...g,
        isSelected: g.id === selectedGeneration,
      })));
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Failed to select logo');
    } finally {
      setSelecting(false);
    }
  };

  // ============================================
  // Color handlers
  // ============================================

  const selectPreset = (preset: (typeof COLOR_PRESETS)[0]) => {
    setPrimaryColor(preset.primary);
    setSecondaryColor(preset.secondary);
    saveData({ primary_color: preset.primary, secondary_color: preset.secondary });
  };

  const handleColorBlur = () => {
    saveData();
  };

  const handleRemoveLogo = () => {
    setLogoUrl('');
    setLogoSource(undefined);
    setSelectedGeneration(null);
    saveData({ logo_url: undefined, logo_source: undefined });
  };

  const handleModeChange = (mode: LogoMode) => {
    setLogoMode(mode);
    if (mode === 'none') {
      setLogoUrl('');
      setLogoSource(undefined);
      saveData({ has_logo: false, logo_url: undefined, logo_source: undefined });
    }
  };

  const handleRecrop = async () => {
    if (!logoUrl) return;
    setTempImageUrl(logoUrl);
    setShowCropper(true);
  };

  // Can we continue?
  const canContinue = logoMode === 'none' || !!logoUrl;

  return (
    <div className="space-y-8">
      {/* Logo Cropper Modal */}
      {showCropper && tempImageUrl && (
        <LogoCropper
          imageUrl={tempImageUrl}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          onSkip={handleCropSkip}
        />
      )}

      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Brand Assets
        </h1>
        <p className="text-gray-600">
          Your logo and brand colors define your website's identity.
        </p>
      </div>

      {/* Test Mode Banner */}
      {generationMode?.mode === 'test' && logoMode === 'generate' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 flex items-center gap-2">
          <Info className="w-4 h-4 flex-shrink-0" />
          <span>Test mode: Logo generation is simulated. No charges will be made.</span>
        </div>
      )}

      {/* Logo Mode Selection */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Your Logo</h2>

        {/* Mode Tabs */}
        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => handleModeChange('upload')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              logoMode === 'upload'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Upload className="w-4 h-4" />
            Upload Logo
          </button>
          {LOGO_GENERATOR_ENABLED && (
            <button
              onClick={() => handleModeChange('generate')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                logoMode === 'generate'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Wand2 className="w-4 h-4" />
              AI Logo Builder
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-semibold">Free Beta</span>
            </button>
          )}
          <button
            onClick={() => handleModeChange('none')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              logoMode === 'none'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Skip for Now
          </button>
        </div>

        {/* Upload Mode */}
        {logoMode === 'upload' && (
          <div className="space-y-4">
            {logoUrl && (logoSource === 'upload' || !LOGO_GENERATOR_ENABLED) ? (
              <div className="space-y-3">
                <div className="relative inline-block">
                  <div className="w-48 h-24 bg-white border-2 border-gray-200 rounded-xl flex items-center justify-center p-4">
                    <img
                      src={logoUrl}
                      alt="Logo preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <button
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    title="Remove logo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleRecrop}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Crop className="w-4 h-4" />
                    Adjust crop
                  </button>
                  <label className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Upload different
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
            ) : (
              <label className="block">
                <div className={`w-full max-w-sm border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  uploadError
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                }`}>
                  <Upload className={`w-8 h-8 mx-auto mb-2 ${uploadError ? 'text-red-400' : 'text-gray-400'}`} />
                  <p className="text-gray-600 font-medium">
                    {uploading ? 'Uploading...' : 'Click to upload logo'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">PNG, JPG, SVG up to 5MB</p>
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}

            {uploadError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{uploadError}</span>
              </div>
            )}
          </div>
        )}

        {/* AI Generation Mode */}
        {LOGO_GENERATOR_ENABLED && logoMode === 'generate' && (
          <div className="space-y-6 p-4 bg-gray-50 rounded-xl">
            {/* Intro */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <Wand2 className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">AI Logo Builder</h3>
                <p className="text-sm text-gray-600">
                  Try our AI logo builder free while it's in beta! Choose a style, add optional hints, and we'll generate up to 3 options. Your feedback helps us improve.
                </p>
              </div>
            </div>

            {/* Generated Logos Gallery */}
            {generations.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Your Generated Logos</h4>
                <div className="flex gap-3 flex-wrap">
                  {generations.map((gen) => (
                    <button
                      key={gen.id}
                      onClick={() => handleSelectGeneration(gen.id)}
                      className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedGeneration === gen.id
                          ? 'border-primary-500 ring-2 ring-primary-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={gen.logoUrl}
                        alt={`Logo option ${gen.roundNumber}`}
                        className="w-full h-full object-contain bg-white p-2"
                      />
                      {gen.isSelected && (
                        <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs py-0.5 text-center capitalize">
                        {gen.style}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Try Again nudge after round 1 */}
            {generations.length === 3 && generationsUsed < generationsAllowed && !selectedGeneration && (
              <p className="text-sm text-gray-500 text-center italic">
                Not loving these? Try another round below with a different style or hint.
              </p>
            )}

            {/* Selected Logo Preview */}
            {selectedGeneration && logoUrl && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Selected Logo</h4>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 bg-white border-2 border-primary-300 rounded-xl flex items-center justify-center p-3">
                    <img
                      src={logoUrl}
                      alt="Selected logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Rate your satisfaction:</p>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => setSatisfactionRating(rating)}
                          className={`p-1 transition-colors ${
                            satisfactionRating && satisfactionRating >= rating
                              ? 'text-yellow-400'
                              : 'text-gray-300 hover:text-yellow-200'
                          }`}
                        >
                          <Star className="w-5 h-5 fill-current" />
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={handleConfirmSelection}
                      disabled={selecting}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {selecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Confirm Selection
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Generation Controls */}
            {generationsUsed < generationsAllowed || generationsAllowed === 0 ? (
              <div className="space-y-4">
                {/* Style Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Style</label>
                  <div className="flex gap-2">
                    {LOGO_STYLES.map((style) => (
                      <button
                        key={style.value}
                        onClick={() => setSelectedStyle(style.value)}
                        className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          selectedStyle === style.value
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <span className="block">{style.label}</span>
                        <span className="block text-xs font-normal opacity-70">{style.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* User Hint */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Hint (optional)
                    <span className="font-normal text-gray-400 ml-1">— {50 - userHint.length} chars left</span>
                  </label>
                  <input
                    type="text"
                    value={userHint}
                    onChange={(e) => setUserHint(e.target.value.slice(0, 50))}
                    placeholder="e.g., include a wrench, use green tones"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
                    maxLength={50}
                  />
                </div>

                {/* Color Confirmation for First Generation */}
                {showColorConfirm && (
                  <div className="p-4 bg-white border-2 border-primary-300 rounded-xl space-y-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Confirm your brand colors</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Your logo will be generated using these colors. Make sure they're correct before continuing.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-lg border border-gray-200"
                          style={{ backgroundColor: primaryColor }}
                        />
                        <span className="text-sm text-gray-600">Primary</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-lg border border-gray-200"
                          style={{ backgroundColor: secondaryColor }}
                        />
                        <span className="text-sm text-gray-600">Secondary</span>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={doGenerate}
                        disabled={generating}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                      >
                        {generating ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4" />
                            Generate with these colors
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowColorConfirm(false)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
                      >
                        Change colors
                      </button>
                    </div>
                  </div>
                )}

                {/* Generate Button */}
                {!showColorConfirm && (
                  <button
                    onClick={handleGenerateClick}
                    disabled={generating}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        {generations.length === 0 ? 'Generate Logo' : `Generate Another (${generationsAllowed - generationsUsed} left)`}
                      </>
                    )}
                  </button>
                )}

                {generations.length > 0 && (
                  <p className="text-xs text-gray-500 text-center">
                    {generationsUsed} of {generationsAllowed} generations used
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-600 text-center py-2">
                All logo options have been generated. Select your favorite above or skip for now.
              </p>
            )}

            {generateError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{generateError}</span>
              </div>
            )}
          </div>
        )}

        {/* No Logo Mode */}
        {logoMode === 'none' && (
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-gray-600 text-sm">
              No problem! We'll use your business name as a text logo. You can always add a logo later from your dashboard.
            </p>
          </div>
        )}
      </div>

      {/* Color extraction loading state */}
      {extractingColors && (
        <div className="flex items-center gap-3 p-4 bg-primary-50 border border-primary-200 rounded-xl">
          <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
          <span className="text-primary-700">Extracting colors from your logo...</span>
        </div>
      )}

      {/* Extracted colors from logo */}
      {showExtractedColors && extractedColors && !extractingColors && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Colors from Your Logo</h2>
          </div>
          <p className="text-sm text-gray-600">
            We found these colors in your logo. They've been applied automatically, but you can change them below.
          </p>
          <div className="flex gap-3">
            {extractedColors.palette.map((color, index) => (
              <button
                key={index}
                onClick={() => {
                  if (index === 0) {
                    setPrimaryColor(color);
                    saveData({ primary_color: color });
                  } else {
                    setSecondaryColor(color);
                    saveData({ secondary_color: color });
                  }
                }}
                className={`
                  w-12 h-12 rounded-lg border-2 transition-all shadow-sm hover:scale-105
                  ${primaryColor === color || secondaryColor === color
                    ? 'border-primary-600 ring-2 ring-primary-200'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                style={{ backgroundColor: color }}
                title={`${color} - Click to use`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Color selection */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Brand Colors</h2>
          <p className="text-sm text-gray-500 mt-1">
            {showExtractedColors && extractedColors
              ? 'Or choose from our curated palettes:'
              : 'Select a preset below or choose your own colors'}
          </p>
        </div>

        {/* Presets */}
        <div className="flex flex-wrap gap-3">
          {COLOR_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => selectPreset(preset)}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all
                ${
                  primaryColor === preset.primary
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <div
                className="w-5 h-5 rounded-full"
                style={{ backgroundColor: preset.primary }}
              />
              <span className="text-sm text-gray-700">{preset.name}</span>
            </button>
          ))}
        </div>

        {/* Custom color pickers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Color
            </label>
            <div className="flex items-center gap-3">
              <label className="relative cursor-pointer group">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  onBlur={handleColorBlur}
                  className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200 group-hover:border-gray-400"
                />
                <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs text-gray-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                  Tap to pick
                </span>
              </label>
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                onBlur={handleColorBlur}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                placeholder="#000000"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1 sm:hidden">Tap the color square to pick, or enter a HEX code</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Color
            </label>
            <div className="flex items-center gap-3">
              <label className="relative cursor-pointer group">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  onBlur={handleColorBlur}
                  className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200 group-hover:border-gray-400"
                />
                <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs text-gray-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                  Tap to pick
                </span>
              </label>
              <input
                type="text"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                onBlur={handleColorBlur}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                placeholder="#000000"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1 sm:hidden">Tap the color square to pick, or enter a HEX code</p>
          </div>
        </div>

        {/* Color preview */}
        <div className="p-4 rounded-xl bg-gray-50">
          <p className="text-sm text-gray-500 mb-3">Preview</p>
          <p className="text-xs text-gray-400 mb-3">These buttons show how your colors will look on your website</p>
          <div className="flex flex-wrap gap-4">
            <button
              className="px-6 py-2 rounded-lg text-white font-medium"
              style={{ backgroundColor: primaryColor }}
            >
              Primary Button
            </button>
            <button
              className="px-6 py-2 rounded-lg text-white font-medium"
              style={{ backgroundColor: secondaryColor }}
            >
              Secondary Button
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={saving || !canContinue}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium bg-primary-600 text-white hover:bg-primary-700 transition-all disabled:opacity-50"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
